import { search } from "@/common/services/search";
import ClickOutside from "vue-click-outside";
import { trackOverLimitQuery, capitalizeFirstLetter } from "@/common/functions";

export default {
	name: "SearchBox",
	data() {
		return {
			query: "",
			results: [],
			showTooltip: false,
			isFocused: false,
			submitButtonPressed: false,
		};
	},
	watch: {
		query(value) {
			this.$emit("input", value);
			this.$store.commit("updateQuery", value);
			if (value.length < 1) this.onClear();
		},
		$route: "setQuery", // call again the method if the route changes
	},
	computed: {
		searchEnabled() {
			if (Number(this.query)) return this.query.length > 0;
			return this.query.length > 1;
		},
		hasError() {
			return this.searchEnabled && this.results.length === 0;
		},
		packCount() {
			let packCount = 0;
			if (!this.hasError) {
				this.results.forEach((item) => {
					if (item.type === "GROUP" || item.type === "PACK") {
						packCount++;
					}
				});
			}
			return packCount;
		},
		exactMatches() {
			const query = this.query
				.trim()
				.replace(/^(pack|level|group)\s/i, "");
			return this.results.filter((result) => {
				return query === result.name;
			});
		},
		resultMessage() {
			// User is not searching for anything.
			if (!this.searchEnabled) return null;

			const resultCount = this.results.length;

			// No search results available.
			if (resultCount === 0) {
				const queryAsNumber = parseInt(this.query);

				if (
					!isNaN(queryAsNumber) &&
					queryAsNumber > this.$store.state.maxLevel
				) {
					trackOverLimitQuery(this.$store, this.query);
					return this.$t("WordscapesSearchErrorText3");
				}

				return this.submitButtonPressed
					? this.$t("WordscapesSearchErrorText2")
					: this.$t("WordscapesSearchErrorText1", {
							userQuery: `"<strong>${this.query}</strong>"`,
					  });
			}

			// One search result, no message needed.
			if (resultCount === 1) return null;

			const exactMatchCount = this.exactMatches.length;

			// No exact matches or only 1 exact match available. No message needed.
			if (exactMatchCount <= 1) return null;

			if (exactMatchCount > 1) {
				return this.$t("WordscapesSearchDisambiguationText", {
					NumberOfPacks: this.packCount,
					UserQuery: this.query,
				});
			}

			return null;
		},
		placeholder() {
			return this.isSmallDevice
				? this.$t("WordscapesSearchPlaceholderMobile")
				: this.$t("WordscapesSearchPlaceholder");
		},
	},
	methods: {
		onFocus() {
			this.$emit("focus");
			this.isFocused = true;
			this.triggerGaEvent("Click on the search bar");
		},
		onBlur() {
			this.$emit("blur");
			this.isFocused = false;
		},
		onInput(el) {
			this.query = el.target.value;
			this.$emit("input", this.query);
			this.triggerGaEvent("Query searched on the search bar");
			if (this.submitButtonPressed) this.submitButtonPressed = false;
			if (this.searchEnabled) {
				this.results = search(this.query, this.$route.name);
				this.results.sort(function(a, b) {
					if (a.type > b.type) {
						return -1;
					} else if (a.type < b.type) {
						return 1;
					}
					if (a.name < b.name) {
						return -1;
					}
					if (a.name > b.name) {
						return 1;
					}
					return 0;
				});
			}
		},
		getResultValue(item) {
			let name = capitalizeFirstLetter(item.name);
			let query = capitalizeFirstLetter(
				this.query.toLowerCase().replace(/^(group|pack|level)\s*/gi, "")
			);

			name = name.replace(query, `<b>${query}</b>`);
			if (item.type === "GROUP" || item.type === "LEVEL") {
				return `${name}`;
			}
			if (item.type === "PACK") {
				return `${name} Levels ${item.min}-${item.max}`;
			}
			return ``;
		},
		handleNavigate(item) {
			if (!item) return;
			if (item.type === "GROUP") {
				this.navigateTo(`/answers/${item.name}/`);
			} else if (item.type === "PACK") {
				this.navigateTo(`/answers/${item.group}/${item.name}/`);
			} else if (item.type === "LEVEL") {
				this.navigateTo(
					`/answers/${item.group}/${item.pack}/${item.name}/`
				);
			}
			this.onBlur();
			this.onClear();
		},
		handleClearButton() {
			this.onClear();
			this.triggerGaEvent(
				`Click on clear button ${this.$t(
					"WordscapesLevelPageClearAriaLabel"
				)}`
			);
		},
		onClear() {
			this.query = "";
			this.results = [];
			this.submitButtonPressed = false;
		},
		handleSubmit() {
			if (this.resultMessage) {
				this.submitButtonPressed = true;
				return false;
			}

			if (this.searchEnabled) {
				this.triggerGaEvent(
					`Click on button ${this.$t(
						"WordscapesLevelPageSearchAriaLabel"
					)}`
				);
			}
			this.$refs.searchInput.blur();

			// If it is a number-based query, automatically pick the first level.
			if (
				(Number(this.query) || /^level\s\d+$/i.test(this.query)) &&
				this.results.length
			) {
				return this.handleNavigate(this.results[0]);
			}

			// If there is only 1 search result, it's safe to navigate to that one.
			if (this.results.length === 1) {
				return this.handleNavigate(this.results[0]);
			}

			// If we only have one exact match, then go ahead and navigate to that one.
			const exactMatches = this.exactMatches;
			if (exactMatches.length === 1) {
				return this.handleNavigate(exactMatches[0]);
			}
		},
		navigateTo(url) {
			this.$store.commit("updateFromSearch", true);
			if (this.$router.currentRoute.path != url)
				this.$router.push({ path: url });
		},
		onTooltipClick() {
			this.showTooltip = !this.showTooltip;

			if (this.showTooltip) {
				this.triggerGaEvent(
					`Click on search tooltip ${this.$t(
						"WordscapesSearchTooltipText"
					)}`
				);
			}
		},
		setQuery() {
			this.query = this.$store.state.query;
			this.$store.commit("updateFromSearch", false);
		},
		triggerGaEvent(eventAction) {
			this.$store.dispatch("trackEvent", {
				eventCategory: "Search",
				eventAction: eventAction,
			});
		},
	},
	mounted() {
		this.setQuery();
	},
	directives: {
		ClickOutside,
	},
};
